import { ArrowDownTrayIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, PaperClipIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { deleteRoverTdmAsset, getRoverTdmAsset } from '@/api/Rover/applicationTDM';
import type { IGetRoverTdmResponseAssetType } from '@/api/Rover/applicationTDM/types';
import type { DeleteTdmAssetPayload } from '@/api/tdm';
import Confirm from '@/components/UI/Modal/Confirm';
import SimpleDropDown, { DropDownItem } from '@/components/UI/SimpleDropDown';
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';
import { notifyUser } from '@/store/Toast';
import type { GetDeliverableInterface, GetMilestoneInterface, GetTaskInterface } from '@/types/Project';
import type { TdmAssetType, TdmType } from '@/types/Tdm';
import { sanitizeResponseError } from '@/utils';
import UploadForm from './UploadForm';

const tdmAssetTypes: { [key: string]: string } = {
  template: 'Template File',
  document: 'Document File',
};

const roverQueryKeyMapping = {
  task: RoverReactQueryKey.GET_ROVER_TASK_ASSETS,
  milestone: RoverReactQueryKey.GET_ROVER_MILESTONE_ASSETS,
  deliverable: RoverReactQueryKey.GET_ROVER_DELIVERABLE_ASSETS,
};

const roverTdmAssetQueryKey = (tdmType: TdmType, tdmId: number) => [roverQueryKeyMapping[tdmType], tdmId] as const;

export default function TdmAttachments({
  tdm,
  tdmType,
}: {
  tdm: GetTaskInterface | GetDeliverableInterface | GetMilestoneInterface;
  tdmType: TdmType;
}) {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentAssetType, setCurrentAssetType] = useState<TdmAssetType>();

  //Tanstack: version 5 query implementation
  const fetchTdmAssets = useQuery({
    queryKey: roverTdmAssetQueryKey(tdmType, tdm.id),
    queryFn: () => getRoverTdmAsset({ tdmId: tdm.id, tdmType }),
  });
  const tdmAssets = fetchTdmAssets?.data?.data;

  const [deleteFileSelected, setDeleteFileSelected] = useState<DeleteTdmAssetPayload>();

  const toggleUploadModal = (assetType: 'template' | 'document') => {
    setCurrentAssetType(assetType);
    setShowModal((prev) => !prev);
  };

  const removeTdmAsset = useMutation(
    (data: DeleteTdmAssetPayload) =>
      deleteRoverTdmAsset({
        assetId: data.id,
        tdmType: tdmType,
        tdmId: tdm.id,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: roverTdmAssetQueryKey(tdmType, tdm.id) });
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Deleted successfully: ${tdmType} ${deleteFileSelected?.assetType ? tdmAssetTypes[deleteFileSelected.assetType] : ''}`,
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );
  const deleteTdmAssetHandler = ({ assetType, key, id }: IGetRoverTdmResponseAssetType) => {
    const data: DeleteTdmAssetPayload = {
      id,
      tdmType,
      assetType,
      key,
    };
    setShowDeleteConfirm(true);
    setDeleteFileSelected(data);
  };

  const confirmDeleteAsset = () => {
    if (deleteFileSelected) {
      removeTdmAsset.mutate(deleteFileSelected);
    } else {
      alert('No file chosen for delete.');
    }
    setShowDeleteConfirm(false);
  };

  const cancelDeleteAsset = () => {
    setShowDeleteConfirm(false);
  };

  const [templates, documents] = tdmAssets?.reduce<[IGetRoverTdmResponseAssetType[], IGetRoverTdmResponseAssetType[]]>(
    (acc, asset) => {
      if (asset.assetType === 'template') {
        acc[0] = [...acc[0], asset];
      }
      if (asset.assetType === 'document') {
        acc[1] = [...acc[1], asset];
      }
      return acc;
    },
    [[], []]
  ) || [[], []];

  return (
    <>
      {showDeleteConfirm && (
        <Confirm
          title='Delete File'
          message={`You are about to delete ${tdm.name} ${tdmType}. Are you sure you want to delete ?`}
          acceptButtonLabel='Delete'
          declineButtonLabel='Cancel'
          accept={confirmDeleteAsset}
          decline={cancelDeleteAsset}
        />
      )}
      <section id='tdm-attachments' aria-labelledby='timeline-title' className='lg:col-start-3 lg:col-span-1'>
        <div className='bg-white px-4 py-5 shadow sm:rounded-lg sm:px-6'>
          <div className='flex justify-between gap-2 items-center'>
            <h2 id='timeline-title' className='m-0 text-base font-medium text-gray-900'>
              Attachments
            </h2>
            <SimpleDropDown
              toggle={
                <div
                  id='tdm-upload-dropdown'
                  className='w-full border-2 flex justify-center items-center py-2 px-4 rounded-md text-neutralColor-normal hover:bg-gray-100 cursor-pointer transition duration-300 ease-in-out gap-2'
                >
                  <ArrowUpTrayIcon className='h-5 w-5' />
                  <p className='font-medium'>Upload</p>
                  <ChevronDownIcon className='h-4 w-4' />
                </div>
              }
            >
              <DropDownItem id='template-file-option' onClick={() => toggleUploadModal('template')}>
                Template File
              </DropDownItem>
              <DropDownItem id='document-file-option' onClick={() => toggleUploadModal('document')}>
                Document File
              </DropDownItem>
            </SimpleDropDown>
          </div>

          <dl id='tdm-templates' className='sm:col-span-2 mt-4'>
            <dt className='text-sm font-medium text-gray-500'>Template</dt>
            {templates.length === 0 && <dd className='my-2'>0 Template files</dd>}
            {templates.map((template) => (
              <dd key={template.id} className='mt-1 p-3 flex border border-gray-200 rounded-md text-sm'>
                <div className='w-0 flex-1 flex items-center'>
                  <PaperClipIcon className='shrink-0 h-5 w-5 text-gray-400' aria-hidden='true' />
                  <span className='ml-2 flex-1 w-0 truncate'>{template.name}</span>
                </div>
                <div className='ml-4 shrink-0 flex gap-4'>
                  <a id='download-template-button' href={template.key} download>
                    <ArrowDownTrayIcon className='h-5 w-5 text-blue-600 hover:text-blue-300 cursor-pointer' />
                  </a>
                  <div id='delete-template-button' onClick={() => deleteTdmAssetHandler(template)}>
                    <TrashIcon className='h-5 w-5 text-red-600 hover:text-red-300 cursor-pointer' />
                  </div>
                </div>
              </dd>
            ))}
          </dl>
          <dl id='tdm-documents' className='sm:col-span-2 mt-4'>
            <dt className='text-sm font-medium text-gray-500'>Document</dt>
            {documents.length === 0 && <dd className='my-2'>0 Document files</dd>}
            {documents.map((document) => (
              <dd key={document.id} className='mt-1 p-3 flex border border-gray-200 rounded-md text-sm'>
                <div className='w-0 flex-1 flex items-center'>
                  <PaperClipIcon className='shrink-0 h-5 w-5 text-gray-400' aria-hidden='true' />
                  <span className='ml-2 flex-1 w-0 truncate'>{document.name}</span>
                </div>
                <div className='ml-4 shrink-0 flex gap-4'>
                  <a id='download-document-button' href={document.key} download>
                    <ArrowDownTrayIcon className='h-5 w-5 text-blue-600 hover:text-blue-300 cursor-pointer' />
                  </a>
                  <div id='delete-document-button' onClick={() => deleteTdmAssetHandler(document)}>
                    <TrashIcon className='h-5 w-5 text-red-600 hover:text-red-300 cursor-pointer' />
                  </div>
                </div>
              </dd>
            ))}
          </dl>
        </div>
      </section>

      {showModal && currentAssetType && (
        <UploadForm tdmAssetType={currentAssetType} tdm={tdm} tdmType={tdmType} handleModal={toggleUploadModal} />
      )}
    </>
  );
}
